import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { useRecoilState, useResetRecoilState } from "recoil";
import { useMutation, useQuery } from "@apollo/react-hooks";
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useMyHrContractsByEmployee } from "my-hr/hooks";
import MyTaxApplicationCardInfoItem from "my-tax/components/application-card-info-item/application-card-info-item";
import MyTaxApplicationHeader from "my-tax/components/application-header/application-header";
import TaxCreditApplicationButtonBars from "my-tax/components/tax-credit-application-buttonbars/tax-credit-application-buttonbars";
import MyTaxCreditApplicationCheckboxLine from "my-tax/components/tax-credit-application-checkbox-line/tax-credit-application-checkbox-line";
import MyTaxCreditApplicationErrorBox from "my-tax/components/tax-credit-application-error-box/tax-credit-application-error-box";
import MyTaxUnderTwentyFiveTaxCreditApplicationPrint from "my-tax/components/under-twenty-five-tax-credit-application-print/under-twenty-five-tax-credit-application-print";
import { MY_TAX_GET_DECLARATION_UNDER_25 } from "my-tax/graphql";
import { MY_TAX_CREATE_UNDER_25_DECLARATION } from "my-tax/graphql";
import { taxDeclarationUnder25 } from "my-tax/states/tax-declaration-under-25";
import { underTwentyFiveAmountDiscountCbState } from "my-tax/states/under-twenty-five-amount-discountcb-state";
import { underTwentyFiveTaxNoApplyOrLimit } from "my-tax/states/under-twenty-five-tax-no-apply-or-limit";
import { OcCard, OcDownload } from "oc/components";
import { useOcCurrentUserState, useOcSnackbar } from "oc/hooks";

type UseFormDataProps = {
  _id: number | undefined;
  setSelected: (params: any) => void;
};

const useFormData = ({ _id, setSelected }: UseFormDataProps) => {
  const { t } = useTranslation();
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [disabledButton, setDisabledButton] = useState(false);
  const [states, setStates] = useRecoilState(taxDeclarationUnder25);
  const resetStates = useResetRecoilState(taxDeclarationUnder25);

  const { data, loading, error } = useQuery(MY_TAX_GET_DECLARATION_UNDER_25, {
    fetchPolicy: "cache-and-network",
    skip: !_id,
    variables: {
      _id: Number.parseInt(_id?.toString() ?? "0"),
    },
  });

  const result = data?.taxDeclarationUnder25;

  // Sync query result to Recoil state (replaces deprecated onCompleted)
  useEffect(() => {
    if (!loading && result) {
      const newStates = { ...result };
      newStates.dateOfDeclaration = new Date(result?.dateOfDeclaration);
      delete newStates.__typename;
      setStates(newStates);
    }
  }, [loading, result, setStates]);

  const handleChange = ({ key, value }: { key: string; value: any }) => {
    const newState = { ...states, [key]: value };
    setStates(newState);
  };

  const handleStateChange = (newState: any) => {
    setStates(newState);
  };

  const { setOpenMessage } = useOcSnackbar();
  const [saveDeclaration, { loading: mutationLoading }] = useMutation(
    MY_TAX_CREATE_UNDER_25_DECLARATION,
    {
      onCompleted: (data) => {
        const res = data?.createTaxDeclarationUnder25;
        setDisabledButton(false);
        if (res?.result) {
          if (res?.messages?.length > 0) {
            const message = res?.messages[0];
            if (
              message === "A korábban beadott nyilatkozata törlésre került!"
            ) {
              setOpenMessage({
                type: "success",
                message: t("MY_TAX_DECLARATION_CREATED_SUCCESSFULLY"),
              });
              setOpenMessage({
                type: "warning",
                message: t("MY_TAX_PREVIOUS_TAX_DECLARATION_DELETED"),
              });
            }
          } else {
            setOpenMessage({
              type: "success",
              message: t("MY_TAX_DECLARATION_CREATED_SUCCESSFULLY"),
            });
          }
          setSaveErrors([]);
          const params = {
            editable: false,
            tableId: res?.tableId,
            year: states?.dateOfDeclaration?.getFullYear().toString(),
            type: "TaxDeclarationUnder25",
            state: "requested",
          };
          setSelected(params);
        } else {
          setSaveErrors(res?.messages);
          setDisabledButton(false);
        }
      },
      onError: () => {
        setSaveErrors([t("MY_TAX_DECLARATION_CREATED_FAILED")]);
        setDisabledButton(false);
      },
    },
  );

  const handleSave = () => {
    setDisabledButton(true);
    const params = { ...states };
    saveDeclaration({ variables: params });
  };

  return {
    data: result,
    loading: loading || mutationLoading,
    error,
    states,
    disabledButton,
    resetStates,
    handleChange,
    handleStateChange,
    handleSave,
    saveErrors,
    setSaveErrors,
  };
};

type MyTaxUnderTwentyFiveTaxCreditApplicationProps = {
  fullName: string;
  taxNumber: string;
  taxApplicationSaveEnabled: boolean;
  handleRemoveRequest: () => void;
  selected: any;
  setSelected: (params: any) => void;
  isMobile: boolean;
};

const MyTaxUnderTwentyFiveTaxCreditApplication = ({
  fullName,
  taxNumber,
  taxApplicationSaveEnabled,
  handleRemoveRequest,
  selected,
  setSelected,
  isMobile,
}: MyTaxUnderTwentyFiveTaxCreditApplicationProps) => {
  const editable = selected?.editable;
  const { t } = useTranslation();
  const history = useHistory();
  const theme = useTheme();

  const {
    states,
    loading,
    error,
    saveErrors,
    disabledButton,
    setSaveErrors,
    resetStates,
    handleChange,
    handleStateChange,
    handleSave,
  } = useFormData({
    _id: selected?.tableId,
    setSelected,
  });

  const showRemoveButton =
    states?.state === "K" || selected?.state === "K" ? true : false;

  const dateOfDeclarationString = new Date(
    states?.dateOfDeclaration,
  ).toLocaleDateString();

  const title =
    "Nyilatkozat a 25 év alatti fiatalok kedvezményének részben vagy egészben történő érvényesítésének mellőzéséről ";

  const componentRef = useRef(null);

  const [underTwentyFiveAmountDiscountCb, setUnderTwentyFiveAmountDiscountCb] =
    useRecoilState(underTwentyFiveAmountDiscountCbState);

  const resetUnderTwentyFiveAmountDiscountCbState = useResetRecoilState(
    underTwentyFiveAmountDiscountCbState,
  );

  const [radioGroupState, setRadioGroupState] = useRecoilState(
    underTwentyFiveTaxNoApplyOrLimit,
  );

  const resetRadioGroupState = useResetRecoilState(
    underTwentyFiveTaxNoApplyOrLimit,
  );

  const handleRadioGroupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === "noApply") {
      const newStates = {
        ...states,
        noApply: true,
        noApplyAboveLimit: false,
        discountLimit: 0,
      };
      setUnderTwentyFiveAmountDiscountCb(false);
      handleStateChange(newStates);
    } else {
      const newStates = {
        ...states,
        noApply: false,
        noApplyAboveLimit: true,
        discountLimit: Number.parseFloat(e.target.value),
      };
      setUnderTwentyFiveAmountDiscountCb(true);
      handleStateChange(newStates);
    }
    setRadioGroupState(e.target.value);
  };

  if (!editable && states?.noApplyAboveLimit && !states?.noApply) {
    setUnderTwentyFiveAmountDiscountCb(true);
  }

  if (!editable && states?.noApplyAboveLimit) {
    setRadioGroupState("limit");
  }

  let { contractCode, employeeCode } = useOcCurrentUserState();
  const { data: contractsData } = useMyHrContractsByEmployee({
    employee: employeeCode,
  });

  let selectedContract = contractsData?.find(
    (item: { code: string }) => item.code === contractCode,
  );

  const handleCancel = () => {
    // Only reset states when in edit mode - when viewing, the states will be
    // overwritten by Apollo data when opening a different declaration
    if (editable) {
      resetUnderTwentyFiveAmountDiscountCbState();
      resetRadioGroupState();
      resetStates();
    }
    setSaveErrors([]);
  };

  const print = useReactToPrint({
    content: () => componentRef.current,
  });

  const handlePrint = () => {
    print();
  };

  const handleScrollToError = () => {
    const errorElement = document.getElementById("errorBox");
    if (errorElement) {
      window.scrollTo({
        top: 300,
        behavior: "smooth",
      });
    }
  };
  useLayoutEffect(() => {
    handleScrollToError();
  }, [saveErrors, history.action]);

  useEffect(() => {
    // Only reset when creating a NEW declaration (editable=true AND no tableId)
    if (editable && !selected?.tableId) {
      resetStates();
    }
    if (history.action === "POP" && editable) {
      return handleCancel();
    }
  }, []); //eslint-disable-line

  return (
    <>
      <Box
        style={{
          marginBottom: "-15px",
        }}
      >
        <Box
          sx={{
            display: "none",
            displayPrint: "block",
            paddingRight: "85px",
          }}
        >
          <MyTaxUnderTwentyFiveTaxCreditApplicationPrint
            ref={componentRef}
            fullName={fullName}
            taxNumber={taxNumber}
            omissionOfApplication={states?.noApply}
            underTwentyFiveAmountDiscount={states?.discountLimit}
            underTwentyFiveAmountDiscountCb={underTwentyFiveAmountDiscountCb}
            employerName={selectedContract?.company?.name || ""}
            employerTaxNumber={selectedContract?.company?.taxNumber || ""}
            applicationDate={dateOfDeclarationString}
          />
        </Box>
        <MyTaxApplicationHeader
          title={title}
          onCancel={handleCancel}
          isMobile={isMobile}
        />
        <Box height="16px" />
        <Box display="flex" justifyContent="space-around">
          <Box
            style={{
              padding: "7px",
              width: "260px",
              borderRadius: "4px",
            }}
          >
            <OcDownload
              iconType="document"
              label="Tájékoztató"
              actionIconType="url"
              href={"#/my-hr/under-twenty-five-tax-credit-information"}
              target="_blank"
              isSmall={true}
            />
          </Box>
        </Box>
        <Box height="16px" />
        {saveErrors.length > 0 && (
          <Box id="errorBox">
            <MyTaxCreditApplicationErrorBox error={saveErrors} />
          </Box>
        )}
        <OcCard
          label="I. A nyilatkozatot adó magánszemély"
          loading={loading}
          error={!!error}
        >
          <Box
            style={{
              padding: "0px 17px",
            }}
          >
            <Box height="52px" />
            <MyTaxApplicationCardInfoItem
              label="Dátum"
              value={dateOfDeclarationString}
              isMobile={isMobile}
            />
            <MyTaxApplicationCardInfoItem
              label="Dolgozó neve"
              value={fullName}
              isMobile={isMobile}
            />
            <MyTaxApplicationCardInfoItem
              label="Adóazonosító jele"
              value={taxNumber}
              isMobile={isMobile}
            />
            <Box height="34px" />
            <Box display="flex">
              <Typography variant="body1" color={editable ? "primary" : "info"}>
                1.
              </Typography>
              <Typography
                display="inline-flex"
                color={editable ? "primary" : "info"}
                style={{
                  textIndent: "16px",
                }}
              >
                Az adóelőleg megállapítása során kérem
              </Typography>
            </Box>
            <Box height="16px" />
            <FormControl
              component="fieldset"
              sx={{
                display: "flex",
              }}
            >
              <Box height="8px" />
              <RadioGroup
                value={radioGroupState}
                aria-label="gender"
                name="controlled-radio-buttons-group"
                onChange={handleRadioGroupChange}
              >
                <Box display="flex" alignItems="center">
                  <Typography
                    variant="body1"
                    color={editable ? "primary" : "info"}
                  >
                    a)
                  </Typography>
                  <Box width="16px" />
                  <FormControlLabel
                    value="noApply"
                    control={
                      <Radio
                        id="omissionOfApplication"
                        style={{
                          color: !editable
                            ? theme.palette.gray60.main
                            : theme.palette.darkBase.main,
                        }}
                        disabled={!editable}
                      />
                    }
                    label={
                      <Typography
                        variant="body1"
                        color={editable ? "primary" : "info"}
                        style={{ alignItems: "center" }}
                      >
                        a 25 év alatti fiatalok kedvezménye érvényesítésének
                        mellőzését.
                      </Typography>
                    }
                  />
                </Box>
                <Box height={isMobile ? "26px" : "10px"} />
                <Box
                  display="flex"
                  alignItems={isMobile ? "flex-start" : "center"}
                >
                  <Box marginTop={isMobile ? "18px" : "0px"}>
                    <Typography
                      variant="body1"
                      color={editable ? "primary" : "info"}
                    >
                      b)
                    </Typography>
                  </Box>
                  <Box width="16px" />
                  <FormControlLabel
                    style={{
                      display: "flex",
                      alignItems: isMobile ? "flex-start" : "center",
                    }}
                    value="limit"
                    control={
                      <Radio
                        id="underTwentyFiveAmountDiscountCb"
                        style={{
                          color: !editable
                            ? theme.palette.gray60.main
                            : theme.palette.darkBase.main,
                          marginTop: isMobile ? "8px" : "0px",
                        }}
                        disabled={!editable}
                      />
                    }
                    label={
                      <MyTaxCreditApplicationCheckboxLine
                        id="amount"
                        text="forintot meghaladó összegre a kedvezmény érvényesítésének mellőzését."
                        textInput={true}
                        textInputType="number"
                        value={
                          states?.discountLimit !== 0
                            ? states?.discountLimit
                            : ""
                        }
                        label={t("OC_AMOUNT")}
                        onChangeInput={(e) =>
                          handleChange({
                            key: "discountLimit",
                            value: Number.parseFloat(
                              e.target.value.replaceAll(/[^0-9]/g, ""),
                            ),
                          })
                        }
                        textInputDisabled={!editable}
                        isMobile={isMobile}
                        editable={editable}
                        checked={false}
                      />
                    }
                  />
                </Box>
              </RadioGroup>
            </FormControl>
          </Box>
          <Box height="36px" />
        </OcCard>
        <Box height="16px" />
        <TaxCreditApplicationButtonBars
          taxApplicationSaveEnabled={taxApplicationSaveEnabled && editable}
          cancelData={handleCancel}
          handlePrint={handlePrint}
          saveData={handleSave}
          editable={editable}
          disabledButton={disabledButton}
          handleRemoveRequest={handleRemoveRequest}
          showModifiedButton={false}
          showRemoveButton={showRemoveButton}
          isMobile={isMobile}
          isModified={false}
          isModifiedDeclaration={false}
        />
      </Box>
    </>
  );
};

export default MyTaxUnderTwentyFiveTaxCreditApplication;
